import {
  ConditionType,
  DayOfWeek,
  OrchestrationCondition,
  OrchestrationCriteria,
  getConditionType,
} from "../../shared/domain/orchestrationCriteria.entity";

/**
 * Byte-for-byte structural mirror of the backend's `ScenarioComparability.kt`
 * (`backoffice-manager/src/main/kotlin/com/witbooking/backofficemanager/service/experiment/domain/ScenarioComparability.kt`).
 * MUST be kept in lockstep with that file — see `scenarioComparability.test.ts` for the parity
 * suite pinning this against `ScenarioComparabilitySpec.kt`'s actual test cases.
 */
export type ComparabilityResult =
  | { comparable: true }
  | {
      comparable: false;
      onlyInA: Set<ConditionType>;
      onlyInB: Set<ConditionType>;
      differentValue: Set<ConditionType>;
      matchModeDiffers: boolean;
    };

// Fixed, deterministic day ordering mirroring java.time.DayOfWeek.getValue() (Monday=1..Sunday=7),
// used only to build a canonical, order-independent join string for activeDays comparisons.
const DAY_ORDER: Record<DayOfWeek, number> = {
  [DayOfWeek.MONDAY]: 1,
  [DayOfWeek.TUESDAY]: 2,
  [DayOfWeek.WEDNESDAY]: 3,
  [DayOfWeek.THURSDAY]: 4,
  [DayOfWeek.FRIDAY]: 5,
  [DayOfWeek.SATURDAY]: 6,
  [DayOfWeek.SUNDAY]: 7,
};

/** Order-independent equality: same elements, any order/duplication - mirrors Kotlin `Set.equals`. */
function setEquals<T>(a: readonly T[] | undefined, b: readonly T[] | undefined): boolean {
  const sa = new Set(a ?? []);
  const sb = new Set(b ?? []);
  if (sa.size !== sb.size) return false;
  for (const v of sa) if (!sb.has(v)) return false;
  return true;
}

/** `undefined`/`null` both normalize to `null` before comparison (Kotlin's `Int?` has no such gap). */
function normalizeMax(max: number | null | undefined): number | null {
  return max ?? null;
}

interface WindowLike {
  startDate?: string;
  endDate?: string;
  activeDays?: DayOfWeek[];
  startHour?: string;
  endHour?: string;
  timeZone?: string;
}

// Canonical sort key for one window, mirroring ScenarioComparability.kt's `windowPeriodComparator`
// exactly: dateRange.start, then endInclusive, then activeDays (sorted, comma-joined), then
// startHour, then endHour, then timeZone - absent string fields sort as "". startDate/endDate are
// zero-padded "YYYY-MM-DD" strings and startHour/endHour are zero-padded "HH:MM" strings (per
// criteriaWindow.helpers.ts's dateToWindowDate/dateToTimeString), so lexicographic string
// ordering already matches chronological ordering - no date parsing is needed to reproduce the
// backend's Instant-based comparator.
function windowSortKey(w: WindowLike): string {
  const days = [...(w.activeDays ?? [])].sort((a, b) => DAY_ORDER[a] - DAY_ORDER[b]).join(",");
  return [
    w.startDate ?? "",
    w.endDate ?? "",
    days,
    w.startHour ?? "",
    w.endHour ?? "",
    w.timeZone ?? "",
  ].join(" ");
}

function sortedWindows<T extends WindowLike>(windows: T[]): T[] {
  return [...windows].sort((a, b) => windowSortKey(a).localeCompare(windowSortKey(b)));
}

/**
 * Returns true when two conditions of the KNOWN-SAME type are semantically equal, using the same
 * per-type normalization as ScenarioComparability.kt's `normalized()`: Set-shaped fields
 * (occupancy/checkInDay/checkOutDay/device/referrer/utmSource/origin/season.seasons values) are
 * compared order-independently; scalar fields (lengthOfStay/numberOfRooms min-max,
 * season.hemisphere) compared directly; bookingWindow/travelWindow lists are sorted before
 * comparison (the one genuinely order-sensitive shape, per the backend's own comment).
 *
 * Precondition: `a`/`b` must have already gone through `OrchestrationCriteriaSchema.parse()` (not
 * raw wire/unparsed input) so that defaults (e.g. `season.hemisphere`) are populated - see
 * scenarioComparability.test.ts's fixtures, which all build criteria via `.parse()`.
 */
function conditionsEqual(
  a: OrchestrationCondition,
  b: OrchestrationCondition,
  type: ConditionType
): boolean {
  switch (type) {
    case ConditionType.OCCUPANCY:
      return setEquals(a.occupancy?.values, b.occupancy?.values);
    case ConditionType.CHECK_IN_DAY:
      return setEquals(a.checkInDay?.activeDays, b.checkInDay?.activeDays);
    case ConditionType.CHECK_OUT_DAY:
      return setEquals(a.checkOutDay?.activeDays, b.checkOutDay?.activeDays);
    case ConditionType.LENGTH_OF_STAY:
      return (
        (a.lengthOfStay?.min ?? 0) === (b.lengthOfStay?.min ?? 0) &&
        normalizeMax(a.lengthOfStay?.max) === normalizeMax(b.lengthOfStay?.max)
      );
    case ConditionType.NUMBER_OF_ROOMS:
      return (
        (a.numberOfRooms?.min ?? 0) === (b.numberOfRooms?.min ?? 0) &&
        normalizeMax(a.numberOfRooms?.max) === normalizeMax(b.numberOfRooms?.max)
      );
    case ConditionType.BOOKING_WINDOW:
      return (
        JSON.stringify(sortedWindows(a.bookingWindow ?? []).map(windowSortKey)) ===
        JSON.stringify(sortedWindows(b.bookingWindow ?? []).map(windowSortKey))
      );
    case ConditionType.TRAVEL_WINDOW:
      return (
        JSON.stringify(sortedWindows(a.travelWindow ?? []).map(windowSortKey)) ===
        JSON.stringify(sortedWindows(b.travelWindow ?? []).map(windowSortKey))
      );
    case ConditionType.DEVICE:
      return setEquals(a.device?.types, b.device?.types);
    case ConditionType.REFERRER:
      return setEquals(a.referrer?.values, b.referrer?.values);
    case ConditionType.UTM_SOURCE:
      return setEquals(a.utmSource?.values, b.utmSource?.values);
    case ConditionType.ORIGIN:
      return setEquals(a.origin?.values, b.origin?.values);
    case ConditionType.SEASON:
      return (
        setEquals(a.season?.seasons, b.season?.seasons) &&
        a.season?.hemisphere === b.season?.hemisphere
      );
    /* istanbul ignore next -- exhaustiveness guard, mirrors the backend's `when` with no `else` */
    default: {
      const exhaustiveCheck: never = type;
      return exhaustiveCheck;
    }
  }
}

function groupByType(
  conditions: OrchestrationCondition[]
): Map<ConditionType, OrchestrationCondition> {
  const map = new Map<ConditionType, OrchestrationCondition>();
  for (const c of conditions) {
    const type = getConditionType(c);
    // last-wins on a hypothetical duplicate type, matching Kotlin's associateBy semantics; in
    // practice this is unreachable for any input that went through OrchestrationCriteriaSchema's
    // `.superRefine` duplicate-type guard (see orchestrationCriteria.entity.ts) - kept as a
    // defensive fallback for a hypothetical unparsed caller only.
    if (type) map.set(type, c);
  }
  return map;
}

/**
 * Byte-for-byte structural port of ScenarioComparability.kt's `compare(a, b)`. MUST be updated in
 * lockstep with any future change to the Kotlin file.
 *
 * Precondition: `a`/`b` must be already-parsed `OrchestrationCriteria` (i.e. gone through
 * `OrchestrationCriteriaSchema.parse()`), never raw wire input.
 */
export function compareScenarioCriteria(
  a: OrchestrationCriteria,
  b: OrchestrationCriteria
): ComparabilityResult {
  const byTypeA = groupByType(a.conditions);
  const byTypeB = groupByType(b.conditions);

  const onlyInA = new Set([...byTypeA.keys()].filter(t => !byTypeB.has(t)));
  const onlyInB = new Set([...byTypeB.keys()].filter(t => !byTypeA.has(t)));
  const commonTypes = [...byTypeA.keys()].filter(t => byTypeB.has(t));

  const differentValue = new Set(
    commonTypes.filter(t => !conditionsEqual(byTypeA.get(t)!, byTypeB.get(t)!, t))
  );

  const matchModeDiffers = a.matchMode !== b.matchMode;

  if (onlyInA.size === 0 && onlyInB.size === 0 && differentValue.size === 0 && !matchModeDiffers) {
    return { comparable: true };
  }
  return { comparable: false, onlyInA, onlyInB, differentValue, matchModeDiffers };
}

export function isComparable(a: OrchestrationCriteria, b: OrchestrationCriteria): boolean {
  return compareScenarioCriteria(a, b).comparable;
}

/**
 * Parses the backend's V7 comparability rejection out of a `grpc-web` `PromiseClient` error.
 * The backend has no structured error-detail field for this - the detail is embedded inside a
 * plain-text `InvalidArgumentException` message, so this is a deliberate string-parsing coupling
 * (see proposal.md's resolved Open Question 1), not a versioned wire contract. Returns `null` for
 * any error that isn't this specific V7 rejection (wrong code, or an INVALID_ARGUMENT for a
 * different reason) so callers fall through to their existing generic error handling.
 */
const CONDITIONS_MISMATCH_PATTERN = /experiment\.scenarios\.conditionsMismatch: onlyInA=\[(.*?)\], onlyInB=\[(.*?)\], differentValue=\[(.*?)\], matchModeDiffers=(true|false)/;

export function parseComparabilityError(error: unknown): ComparabilityResult | null {
  if (
    !error ||
    typeof error !== "object" ||
    !("code" in error) ||
    (error as { code: unknown }).code !== 3
  ) {
    return null; // not an INVALID_ARGUMENT rejection at all
  }
  const message = "message" in error ? String((error as { message: unknown }).message) : "";
  const match = message.match(CONDITIONS_MISMATCH_PATTERN);
  if (!match) return null; // an INVALID_ARGUMENT for a different reason - not this story's concern

  // The backend's message renders Kotlin's `ConditionType` enum CONSTANT names
  // (e.g. "OCCUPANCY", "CHECK_IN_DAY"), which match this repo's `ConditionType` enum KEYS -
  // not its (camelCase) string VALUES used to key into `OrchestrationCondition` - so each raw
  // name must be looked up via the enum's key, not cast directly.
  const parseSet = (raw: string): Set<ConditionType> =>
    new Set(
      raw
        .split(",")
        .map(s => s.trim())
        .filter(Boolean)
        .map(name => ConditionType[name as keyof typeof ConditionType])
        .filter((type): type is ConditionType => type !== undefined)
    );

  return {
    comparable: false,
    onlyInA: parseSet(match[1]),
    onlyInB: parseSet(match[2]),
    differentValue: parseSet(match[3]),
    matchModeDiffers: match[4] === "true",
  };
}
